import React, { useEffect, useRef, useState } from 'react'
import leaf from 'leaflet'
import { GeoSearchControl, OpenStreetMapProvider } from 'leaflet-geosearch';
import 'leaflet-geosearch/assets/css/leaflet.css';

/* TEMPLATE FOR FORM

  // Allow map component selected coordinates to translate to form latitude and longitude fields
  coordinates = null

  validate = async values => {
    const update = {}

    if(this.coordinates) {
      update.latitude = this.coordinates[0]
      update.longitude = this.coordinates[1]
    }

    return update
  }
*/
  
// OpenMapLeaf Component hardcoded to work with the latitude and longitude named fields exactly as spelt
const OPENMAPFIELDS = (comp, form, formRef, addressField = null, hideCoordinates = true) => [
    {
        heading: `<p>If you know the exact Coordinates, you can enter them below. Otherwise, use the map to pinpoint the location.</p>`,
        hide: hideCoordinates
    },
    {
      name: 'latitude',
      type: 'number',
      save: true,
      hide: hideCoordinates,
      required: false,
      // Set view value as string to show negative numbers
      view: {
        value: v => `${v} lat`
      }
    },
    {
      name: 'longitude',
      type: 'number',
      save: true,
      hide: hideCoordinates,
      required: false,
      // Set view value as string to show negative numbers
      view: {
        value: v => `${v} lng`
      }
    },

    // Include Map Component
    {
      name: 'gps_coordinates',
      // Customer View
      heading: 
               (<div className='mb-3'>
                   <center>
                       <h5>Pinpoint Location on Map</h5>
                       <OpenMapLeaf comp={comp} form={form} formRef={formRef} addressField={addressField}/>
                       <span>Click/tap a spot on the map to pinpoint a location</span>
                   </center>
               </div>),
      // Officer View
      view: {
        element: (value, form) => form.latitude ? (
          <OpenMapLeaf comp={comp} form={form} formRef={formRef} addressField={addressField} showMap={true} />
        ) : null },
      save: false,
    }
  ]



const OpenMapLeaf = ({ comp, form, formRef, addressField, showMap = false }) => {
  // Used for showMap render
  const openmapURL = `https://www.openstreetmap.org/?mlat=${form.latitude}&mlon=${form.longitude}#map=17/${form.latitude}/${form.longitude}`;
  // console.log(form, openmapURL);

  const containerStyle = {
    width: '85%',
    height: '620px',
    marginBottom: '20px', // Add margin at the bottom
    zIndex: '1',
  };

  const defaultCenter = {center: [25.0343, -77.3963], zoom: 14}; // Latitude , Longitude

  // Navigator Geolocation object options
  const options = {
    enableHighAccuracy: true,
    timeout: 25000,
    maximumAge: 0,
  };

  const [currentPosition, setCurrentPosition] = useState(defaultCenter) // []
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [mapClicked, setMapClicked] = useState(false);

  const mapRef = useRef(null);
  const containerRef = useRef(null);
  // const markerRef = useRef(null);

  const provider = new OpenStreetMapProvider();
  const searchControl = new GeoSearchControl({ provider: provider, style: 'bar', autoComplete: true, autoCompleteDelay: 250, acceptLanguage: 'en' });

  // Navigator Gelocation getCurrentPosition() callbacks, Success and Error
  const success = (pos) => {
    const crd = pos.coords;
  
    // console.log("Your current position is:");
    // console.log(`Latitude : ${crd.latitude}`);
    // console.log(`Longitude: ${crd.longitude}`);
    // console.log(`More or less ${crd.accuracy} meters.`);

    setCurrentPosition({center: [crd.latitude, crd.longitude], zoom: 14})
  }
  
  const error = (err) => {
    console.warn(`ERROR(${err.code}): ${err.message}`);
  }

  // Generate Leaflet map using OpenStreetMap
  useEffect(() => {
    // Generate a map with default co-ordinates on component load
    if (!showMap && !mapRef.current) {
      mapRef.current = leaf.map('Map', currentPosition);
      leaf.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(mapRef.current);
      mapRef.current.addControl(searchControl);

      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(success, error, options)
      } else {
        console.log("Geolocation not supported");
      }
    }

    // Cleanup when component unmounts
    return () => {
      if (mapRef.current) {
        mapRef.current.removeControl(searchControl);
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [])

  useEffect(() => {
    // Set map default co-ordinates to current user position of Navigator geolocation was allowed
    if (!showMap && mapRef.current) mapRef?.current?.setView(currentPosition.center, 14);
  }, [currentPosition])
  
  // Invalidate map size on container resize to ensure proper map display - can happen when keyboard opens on mobile or when desktop browser window is resized
  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (!showMap && mapRef.current) {
        mapRef.current.invalidateSize();
      }
    });
    if (!showMap && containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  // Add click event to map to get coordinates of clicked location and set form latitude and longitude values accordingly
  useEffect(() => {
    const onMapClick = (e) => {
      setMapClicked(true);
      var popup = leaf.popup();
      // console.log(e.latlng);
      setLatitude(e.latlng.lat);
      setLongitude(e.latlng.lng);

      popup
          .setLatLng(e.latlng)
          .setContent(e.latlng.toString())
          .openOn(mapRef.current);
    }

    if (mapRef.current) {
      mapRef.current.on('click', onMapClick);
    }
  }, [mapRef.current])

  // Whenever latitude or longitude state updates, update form values and refresh form fields to show updated values
  useEffect(() => {
    if (!showMap && comp) {
      comp.coordinates = [latitude, longitude];
    }
    if (!showMap && formRef?.refreshFields && mapClicked) {
      formRef.refreshFields({ latitude, longitude });
      setMapClicked(false); // Reset mapClicked to allow future updates from map clicks
    }
  }, [latitude, longitude])

  // If form latitude and longitude values are updated externally (e.g. applicant editing lat and lng fields), update map view and marker accordingly
  useEffect(() => {
    if (!showMap && form.latitude && form.longitude) {
      if (isNaN(form.latitude) || isNaN(form.longitude)) { return null } // Guard against non-numeric values
      if (mapClicked) return; // If map was already clicked to set coordinates, do not try to override with form values as it can create a loop of updates between map click and form value change

      var popup = leaf.popup();
      // console.log(e.latlng);
      setLatitude(form.latitude);
      setLongitude(form.longitude);

      popup
          .setLatLng([form.latitude, form.longitude])
          .setContent(`[${form.latitude}, ${form.longitude}]`)
          .openOn(mapRef.current);
    }
  }, [form.latitude, form.longitude])

  // When a search is made on the built-in search bar of the map, update form address field (if provided)
  useEffect(() => {
    const setAddress = async (res) => {
      // console.log(res);
      if (!showMap && res) {
        const { label } = res.location;
        
        if (formRef?.refreshFields && addressField) {
          formRef.refreshFields({ [addressField]: label });
        }
      }
    }
    if (mapRef.current) {
      mapRef.current.on('geosearch/showlocation', (result) => setAddress(result));
    }

  }, [mapRef.current, addressField])

  // Return an OpenMap URL if showMap prop is true otherwise return map widget
  return (
    showMap ? 
    <a href={openmapURL} target='_blank' rel='noreferrer'>(Click here for Map Location)</a>
    : 
    <div id='Map' ref={containerRef} style={containerStyle}/>
  )
}

export { OpenMapLeaf, OPENMAPFIELDS };
